'use client';

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trash2, Plus, Minus, ShoppingBag, ArrowRight, ShieldCheck, Sparkles } from 'lucide-react';
import { useCommerce } from '@/context/CommerceContext';
import Link from 'next/link';

export default function CartDrawer() {
  const {
    cart,
    isCartOpen,
    setIsCartOpen,
    removeFromCart,
    updateQuantity,
    cartSubtotal,
    setIsCheckoutOpen,
  } = useCommerce();

  const totalItemsCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const freeDeliveryThreshold = 999;
  const deliveryFee = cartSubtotal >= freeDeliveryThreshold || cartSubtotal === 0 ? 0 : 50;
  const taxAmount = Math.round(cartSubtotal * 0.05); // 5% GST
  const grandTotal = cartSubtotal + deliveryFee + taxAmount;

  return (
    <AnimatePresence>
      {isCartOpen && (
        <div className="fixed inset-0 z-[10000] overflow-hidden">
          {/* Dark Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-xs transition-opacity"
          />

          {/* Drawer Wrapper */}
          <div className="fixed inset-y-0 right-0 max-w-full flex w-full sm:w-auto justify-end pointer-events-none">
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 260 }}
              className="w-full sm:w-[440px] max-w-full bg-[#FFFFFF] shadow-2xl flex flex-col border-l border-[#E09F55]/30 h-full pointer-events-auto"
            >
              {/* 1. Header */}
              <div className="px-5 py-4 border-b border-[#DDDAC4] bg-white flex items-center justify-between shrink-0">
                <div className="flex items-center space-x-2.5">
                  <div className="w-8 h-8 rounded-xl bg-[#950E09]/10 text-[#950E09] flex items-center justify-center shrink-0">
                    <ShoppingBag className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-base sm:text-lg text-[#171711] leading-tight">
                      Your Heritage Cart
                    </h3>
                    <span className="text-[11px] text-[#171711]/60 font-sans block">
                      {totalItemsCount} {totalItemsCount === 1 ? 'item' : 'items'} • Daily Fresh Kitchen Batches
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="p-2 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-[#171711] transition-colors cursor-pointer"
                  aria-label="Close cart drawer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>



              {/* 3. Scrollable Items List */}
              <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5 space-y-3 font-sans">
                {cart.length === 0 ? (
                  <div className="text-center py-20 px-4 space-y-4 font-sans my-auto">
                    <div className="w-16 h-16 rounded-full bg-amber-50 text-amber-700 border border-amber-200/70 flex items-center justify-center mx-auto shadow-xs">
                      <ShoppingBag className="w-8 h-8 text-[#950E09]" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="font-display font-bold text-lg text-[#171711]">Your cart is empty</h4>
                      <p className="text-xs text-neutral-500 max-w-xs mx-auto leading-relaxed">
                        Handcrafted sweets, artisanal Kai Murukku, and pure coconut oil chips are made fresh daily.
                      </p>
                    </div>
                    <button
                      onClick={() => setIsCartOpen(false)}
                      className="px-6 py-2.5 rounded-xl bg-[#950E09] hover:bg-[#800C07] text-[#F7F4D6] text-xs font-bold uppercase tracking-wider shadow-md shadow-[#950E09]/20 transition-all cursor-pointer"
                    >
                      Explore Fresh Delicacies
                    </button>
                  </div>
                ) : (
                  cart.map((item) => (
                    <div
                      key={item.id}
                      className="p-3 sm:p-3.5 rounded-2xl bg-white border border-[#DDDAC4] shadow-xs flex items-center gap-3 hover:border-amber-400/60 transition-colors"
                    >
                      <img
                        src={item.image || '/images/pouch_kai_murukku.png'}
                        alt={item.name}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/images/pouch_kai_murukku.png';
                        }}
                        className="w-16 h-16 sm:w-18 sm:h-18 rounded-xl object-cover shrink-0 border border-neutral-200 bg-neutral-50"
                      />
                      <div className="flex-1 min-w-0 space-y-1">
                        <h4 className="font-display font-bold text-xs sm:text-sm text-[#171711] leading-snug line-clamp-2">
                          {item.name}
                        </h4>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] sm:text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 rounded-md">
                            Weight: {item.weight}
                          </span>
                          <span className="text-[11px] text-neutral-400 font-mono">
                            ₹{item.unitPrice} each
                          </span>
                        </div>
                        <div className="font-display font-bold text-sm text-[#950E09]">
                          ₹{item.unitPrice * item.quantity}
                        </div>
                      </div>

                      {/* Stepper + Delete */}
                      <div className="flex flex-col items-end justify-between gap-3 shrink-0">
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-neutral-400 hover:text-rose-600 p-1 rounded-md hover:bg-rose-50 transition-colors cursor-pointer"
                          title="Remove item"
                          aria-label={`Remove ${item.name} from cart`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>

                        <div className="flex items-center bg-[#FAFBF9] border border-[#DDDAC4] rounded-lg p-0.5 shadow-2xs">
                          <button
                            onClick={() => updateQuantity(item.id, -1)}
                            className="w-6 h-6 rounded flex items-center justify-center text-neutral-700 hover:bg-white hover:text-rose-700 active:scale-95 text-xs font-bold transition-all cursor-pointer"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-xs font-bold text-[#171711] px-2 font-mono text-center min-w-[20px]">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, 1)}
                            className="w-6 h-6 rounded flex items-center justify-center text-neutral-700 hover:bg-white hover:text-emerald-700 active:scale-95 text-xs font-bold transition-all cursor-pointer"
                            aria-label="Increase quantity"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* 4. Drawer Footer */}
              {cart.length > 0 && (
                <div className="p-4 sm:p-5 bg-white border-t border-[#DDDAC4] space-y-3.5 shrink-0 shadow-[0_-8px_20px_rgba(0,0,0,0.03)] font-sans">
                  {/* Price Breakdown */}
                  <div className="space-y-1.5 text-xs text-[#171711]/80">
                    <div className="flex justify-between items-center">
                      <span>Items Subtotal</span>
                      <span className="font-semibold text-neutral-900 font-mono">₹{cartSubtotal}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Delivery Fee</span>
                      <span>
                        {deliveryFee === 0 ? (
                          <strong className="text-emerald-700 font-bold uppercase tracking-wider text-[11px]">FREE</strong>
                        ) : (
                          <span className="font-mono">₹{deliveryFee}</span>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-neutral-500">
                      <span>Estimated GST (5%)</span>
                      <span className="font-mono">₹{taxAmount}</span>
                    </div>
                    <div className="flex justify-between items-baseline pt-2.5 border-t border-neutral-200">
                      <div>
                        <span className="font-display font-bold text-sm text-[#171711] block">Grand Total</span>
                        <span className="text-[10px] text-neutral-400">All taxes and duties included</span>
                      </div>
                      <span className="font-display font-bold text-xl sm:text-2xl text-[#950E09] tracking-tight">
                        ₹{grandTotal}
                      </span>
                    </div>
                  </div>

                  {/* Proceed to Checkout Button */}
                  <Link
                    href="/checkout"
                    onClick={() => {
                      setIsCartOpen(false);
                    }}
                    className="w-full py-3.5 sm:py-4 px-4 rounded-xl sm:rounded-2xl bg-[#950E09] hover:bg-[#800C07] active:scale-[0.99] text-[#F7F4D6] font-bold text-xs sm:text-sm uppercase tracking-wider shadow-lg shadow-[#950E09]/30 flex items-center justify-center space-x-2 transition-all cursor-pointer"
                  >
                    <span>Proceed to Secure Checkout</span>
                    <ArrowRight className="w-4 h-4 text-[#F2BC6F]" />
                  </Link>

                  {/* Trust Footer */}
                  <div className="flex items-center justify-center gap-1.5 text-[10px] text-neutral-500 pt-0.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Pure Heritage Recipe • Nitrogen Freshness Seal • Safe Checkout</span>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}
